const BASE_URL = process.env.REACT_APP_API_API ?? '';
const SESSION_KEY = 'trading_session';

export function getToken(): string | null {
    return window.localStorage.getItem(SESSION_KEY);
}

export function setToken(token: string) {
    window.localStorage.setItem(SESSION_KEY, token);
}

export function clearToken() {
    window.localStorage.removeItem(SESSION_KEY);
}

export function hasSession(): boolean {
    return getToken() !== null;
}

function handleUnauthorized() {
    if (window.location.pathname.startsWith('/login')) return;
    clearToken();
    window.location.href = '/login';
}

type RequestBody = Record<string, unknown> | unknown[];
type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

async function request(path: string, method: Method = 'GET', body?: RequestBody): Promise<Response> {
    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers['Content-Type'] = 'application/json';

    const response = await fetch(`${BASE_URL}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (response.status === 401) {
        handleUnauthorized();
    }

    return response;
}

export async function readErrorMessage(response: Response): Promise<string> {
    const payload = await response.json().catch(() => ({}));
    if (typeof payload?.detail === 'string') return payload.detail;
    return `Request failed (${response.status})`;
}

function readErrorText(value: unknown): string | null {
    if (typeof value === 'string') return value.trim() || null;
    if (!value || typeof value !== 'object') return null;

    const record = value as Record<string, unknown>;
    for (const key of ['msg', 'message'] as const) {
        const text = record[key];
        if (typeof text === 'string' && text.trim()) return text.trim();
    }
    return null;
}

function formatErrorDetail(value: unknown): string | null {
    if (!Array.isArray(value)) return readErrorText(value);

    const messages = value.flatMap((item) => {
        const message = readErrorText(item);
        if (!message) return [];

        if (!item || typeof item !== 'object') return [message];
        const location = (item as Record<string, unknown>).loc;
        if (!Array.isArray(location)) return [message];

        const path = location
            .filter((part) => part !== 'body')
            .map(String)
            .join('.');
        return [path ? `${path}: ${message}` : message];
    });

    return messages.length ? messages.join('; ') : null;
}

export function getApiErrorMessage(payload: unknown, fallback: string): string {
    if (!payload || typeof payload !== 'object') return fallback;
    const record = payload as Record<string, unknown>;
    return formatErrorDetail(record.error) ?? formatErrorDetail(record.detail) ?? fallback;
}

export const get = (path: string) => request(path, 'GET');
export const post = (path: string, body?: RequestBody) => request(path, 'POST', body);
export const put = (path: string, body?: RequestBody) => request(path, 'PUT', body);
export const patch = (path: string, body?: RequestBody) => request(path, 'PATCH', body);
export const del = (path: string) => request(path, 'DELETE');
